import {
  Button,
  FormError,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { apiFetch } from "../../api";
import type { Connector, Project } from "../../configTypes";
import { CONNECTOR_TYPES } from "../../configTypes";
import { Breadcrumbs } from "./Breadcrumbs";

interface CreateConnectorFormProps {
  name: string;
  setName: (v: string) => void;
  type: string;
  setType: (v: string) => void;
  prefix: string;
  setPrefix: (v: string) => void;
  submitting: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

function CreateConnectorForm({
  name,
  setName,
  type,
  setType,
  prefix,
  setPrefix,
  submitting,
  error,
  onSubmit,
  onCancel,
}: CreateConnectorFormProps) {
  return (
    <form onSubmit={onSubmit}>
      <div className="flex gap-4">
        <div className="mb-4 flex-1">
          <Label htmlFor="connector-name" className="form-label">
            Name
          </Label>
          <Input
            id="connector-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter value"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- single-purpose route page; focus initial field on mount
            autoFocus
            autoComplete="one-time-code"
          />
        </div>
        <div className="mb-4 flex-1">
          <Label htmlFor="connector-type" className="form-label">
            Type
          </Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger id="connector-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONNECTOR_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                  {"caption" in t && t.caption ? ` (${t.caption})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mb-4">
        <Label htmlFor="connector-prefix" className="form-label">
          Prefix
        </Label>
        <Input
          id="connector-prefix"
          type="text"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="e.g. ps, sw"
          autoComplete="one-time-code"
        />
      </div>
      {error && <FormError assertive>{error}</FormError>}
      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !name.trim() || !prefix.trim()}>
          {submitting ? "Creating..." : "Create"}
        </Button>
      </div>
    </form>
  );
}

export function CreateConnector() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") ?? "";

  const [projectName, setProjectName] = useState<string>("");
  const [name, setName] = useState("");
  const [type, setType] = useState<string>(CONNECTOR_TYPES[0].value);
  const [prefix, setPrefix] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    void apiFetch<Project>(`/api/projects/${projectId}`).then((p) => setProjectName(p.name));
  }, [projectId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !prefix.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const connector = await apiFetch<Connector>("/api/connectors", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), type, prefix: prefix.trim(), projectId }),
      });
      void navigate(`/config/connectors/${connector.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create connector");
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          { label: projectName || "...", to: `/config/projects/${projectId}` },
          { label: "New Connector" },
        ]}
      />
      <h2 className="mb-6 text-2xl text-foreground">Create Connector</h2>
      <div className="form-card max-w-[600px]">
        <CreateConnectorForm
          name={name}
          setName={setName}
          type={type}
          setType={setType}
          prefix={prefix}
          setPrefix={setPrefix}
          submitting={submitting}
          error={error}
          onSubmit={(e) => void handleSubmit(e)}
          onCancel={() => void navigate(-1)}
        />
      </div>
    </div>
  );
}
